import { useState, useRef } from "react";
import { Card, Form, Row, Col, Button, Table } from "react-bootstrap";
import { IntentionsWithoutPresiderPrintout } from "../../../components/print-components/intentions-without-presider";
import { formatDateTime } from "../../../utils/stringFunctions";
import { Spinner } from "react-bootstrap";
import { useReactToPrint } from "react-to-print";
import { Mass } from "vincent-types/models";
import moment from "moment";
import "./styles.css";
import axios from "axios";

interface IntentionReportEntry {
  startDateTime: string;
  subject: string;
}

interface IntentionsTableProps {
  intentions: IntentionReportEntry[];
  error?: string;
  height?: string;
}

function IntentionsTable(props: IntentionsTableProps) {
  const { intentions, error } = props;
  return (
    <div
      className="intentions-by-presider-table-container"
      style={{ maxHeight: props.height || "400px" }}
    >
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Intention</th>
            <th>{`Mass Date & Time`}</th>
          </tr>
        </thead>
        <tbody>
          {!error && intentions.length === 0 && (
            <tr>
              <td colSpan={2} align="center">
                No Intentions Found
              </td>
            </tr>
          )}
          {!error &&
            intentions.map((i) => (
              <tr>
                <td>{i.subject}</td>
                <td>{formatDateTime(i.startDateTime)}</td>
              </tr>
            ))}
          {error && (
            <tr>
              <td colSpan={3} align="center">
                {error}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

function IntentionsWithoutPresider() {
  const [intentions, setIntentions] = useState<IntentionReportEntry[]>([]);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState<{
    intentions: IntentionReportEntry[];
    printDateTime?: string;
  }>({
    intentions: [],
  });
  const componentRef = useRef<any>();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const search = async () => {
    setIntentions([]);
    setLoading(true);
    setLoaded(false);
    setError("");
    try {
      const resp = await axios.get(
        `/api/mass/instances?start=${startDate}&end=${endDate}`
      );
      const masses = resp.data as Mass[];
      const myIntentions: IntentionReportEntry[] = masses
        .filter((m: Mass) => m.intention)
        .filter((m: Mass) => !m.intention?.priest)
        .map((m: Mass) => {
          return {
            startDateTime: m.startDateTime,
            subject: m.intention?.subject || "",
          };
        });
      setIntentions(myIntentions);
      setReport({
        printDateTime: moment().format("dddd, MMM Do YYYY h:mma"),
        intentions: myIntentions || [],
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || "Error retrieving information");
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  return (
    <div>
      <h2>Reports</h2>
      <Card>
        <Card.Body className="new-intention-card">
          <Card.Title>Intentions without Presider</Card.Title>
          <hr />
          <Form>
            <Row>
              <Col md={4}>
                <Form.Group controlId="formFirstName">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    value={startDate}
                    type="date"
                    onChange={(event) => {
                      setStartDate(event.target.value);
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formLastName">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    value={endDate}
                    type="date"
                    onChange={(event) => {
                      setEndDate(event.target.value);
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
          <div className="intentions-by-presider-button-group">
            <Button disabled={!startDate || !endDate} onClick={search}>
              {loading ? <Spinner animation="border" size="sm" /> : "Search"}
            </Button>
            <Button
              style={{
                visibility: intentions.length > 0 ? "visible" : "hidden",
              }}
              onClick={handlePrint}
            >
              Print
            </Button>
          </div>
          {loaded && (
            <div>
              <IntentionsTable intentions={intentions} error={error} />
            </div>
          )}
        </Card.Body>
      </Card>
      <div style={{ display: "none" }}>
        <IntentionsWithoutPresiderPrintout ref={componentRef} report={report} />
      </div>
    </div>
  );
}

export default IntentionsWithoutPresider;
